import React, { useImperativeHandle, useState } from 'react'
import classNames from 'classnames'
import { ELandingFaqTermsAnimationDelay } from '@/components/composed/landingBlocks/LandingFAQ/constants'
import CollapsingBlockView from '@/components/ui/CollapsingBlock/CollapsingBlockView'
import { formatLocalizedString } from '@/utils/formatUtils'
import { LandingAnimation } from '../../../../components/composed/landingBlocks/LandingAnimation'
import { LANDING_ANIMATION } from '../../../../components/composed/landingBlocks/LandingAnimation/constants'
import type { TLandingFAQProps } from './types'
import './styles.scss'

export const LandingFAQ: React.FC<TLandingFAQProps> = ({
  title,
  items,
  className,
  itemsValues = {},
  withAnimation,
  actionsRef,
  onExpandFAQItem,
}) => {
  const [isExpandedMap, setIsExpandedMap] = useState<Record<string, boolean | undefined>>(Object.create(null))

  const handleToggle = (question: string) => () => {
    const isExpanded = isExpandedMap[question]
    const newValue = !isExpanded

    setIsExpandedMap({
      ...isExpandedMap,
      [question]: newValue,
    })

    if (isExpanded) {
      onExpandFAQItem?.(question)
    }
  }

  useImperativeHandle(
    actionsRef,
    () => ({
      openQuestion: (question: string) => {
        setIsExpandedMap(map => ({
          ...map,
          [question]: true,
        }))
      },
    }),
    [],
  )

  return (
    <section className={classNames('landingFAQ', className)}>
      <div className="landingFAQ__wrapper page-wrapper">
        <LandingAnimation
          desktopTargetStyles={{ ...LANDING_ANIMATION.fadeUp, delay: ELandingFaqTermsAnimationDelay.TITLE }}
          isDisabled={!withAnimation}
        >
          <h2 className="landingFAQ__title">{title}</h2>
        </LandingAnimation>

        <LandingAnimation
          desktopTargetStyles={{ ...LANDING_ANIMATION.fadeUp, delay: ELandingFaqTermsAnimationDelay.LIST }}
          isDisabled={!withAnimation}
        >
          <div className="landingFAQ__questions">
            {items.map(({ id, question, answer }) => (
              <CollapsingBlockView
                id={id}
                key={question}
                title={question}
                headingClass="landingFAQ__spoilerTrigger h4"
                contentClassName="landingFAQ__answer"
                buttonClassName="landingFAQ__button"
                toggleClassName="landingFAQ__toggle"
                isExpanded={isExpandedMap[question] === true}
                onToggle={handleToggle(question)}
              >
                {formatLocalizedString(answer, {
                  Title: text => <h4 className="h5 landingFAQ__answerTitle">{text}</h4>,
                  ul: text => <ul className="landingFAQ__answerList">{text}</ul>,
                  li: text => <li className="landingFAQ__answerListItem">{text}</li>,
                  p: text => <p className="landingFAQ__answerParagraph">{text}</p>,
                  ...itemsValues,
                })}
              </CollapsingBlockView>
            ))}
          </div>
        </LandingAnimation>
      </div>
    </section>
  )
}
